import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Line, Bar, Radar, Doughnut } from 'react-chartjs-2';
import { 
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, 
  BarElement, Title, Tooltip, Filler, Legend, RadialLinearScale, RadarController, 
  ArcElement, DoughnutController 
} from 'chart.js';
import './App.css';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement, 
  Title, Tooltip, Filler, Legend, RadialLinearScale, RadarController, 
  ArcElement, DoughnutController
);
ChartJS.defaults.color = '#9ca3af';
ChartJS.defaults.borderColor = '#333333';

// --- SVG ICON HELPER COMPONENT ---
const Icon = ({ name, size = 20 }) => {
  const icons = {
    dashboard: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9"></rect><rect x="14" y="3" width="7" height="5"></rect><rect x="14" y="12" width="7" height="9"></rect><rect x="3" y="16" width="7" height="5"></rect></svg>,
    trades: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect></svg>,
    risk: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>,
    calendar: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>,
    analytics: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>,
    playbook: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path></svg>,
    settings: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>,
    chevronLeft: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>,
    chevronRight: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>,
    coffee: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8h1a4 4 0 0 1 0 8h-1"></path><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path><line x1="6" y1="1" x2="6" y2="4"></line><line x1="10" y1="1" x2="10" y2="4"></line><line x1="14" y1="1" x2="14" y2="4"></line></svg>
  };
  return icons[name] || null;
};

// --- DATA & MATH HELPERS ---
const formatDateStr = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  return `${month}-${day}-${year}`;
};

const formatTimeStr = (timeStr) => {
  if (!timeStr) return '';
  let [hours, minutes] = timeStr.split(':');
  hours = parseInt(hours, 10);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${ampm}`;
};

const getDuration = (entryDate, entryTime, exitDate, exitTime) => {
  if (!entryDate || !entryTime || !exitDate || !exitTime) return '-';
  const start = new Date(`${entryDate}T${entryTime}`);
  const end = new Date(`${exitDate}T${exitTime}`);
  const diffMs = end - start;
  if (diffMs < 0) return 'Invalid Time';
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m`;
  const diffHrs = Math.floor(diffMins / 60);
  const remainderMins = diffMins % 60;
  if (diffHrs < 24) return `${diffHrs}h ${remainderMins}m`;
  const diffDays = Math.floor(diffHrs / 24);
  const remainderHrs = diffHrs % 24;
  return `${diffDays}d ${remainderHrs}h`;
};

// Universal PnL Calculator for Exits
const calcTradeExitPnl = (trade, exit) => {
  const asset = trade.assetClass || 'Options';
  const isShort = trade.direction === 'Short';
  const priceDiff = isShort ? (trade.entry - exit.price) : (exit.price - trade.entry);
  const q = Number(exit.qty || exit.contracts || 0);
  const totalQ = Number(trade.qty || trade.contracts || 1);
  
  let gross = 0;
  if (asset === 'Options') {
      gross = (exit.price - trade.entry) * q * 100;
  } else if (asset === 'Futures') {
      gross = priceDiff * q * Number(trade.multiplier || 1);
  } else { 
      gross = priceDiff * q;
  }
  
  const comm = Number(trade.commission || 0) * (q / totalQ);
  return gross - comm;
};

const SETUP_OPTIONS = [
  "Breakout / Breakdown", "Support / Resistance Bounce", "Trend Continuation",
  "Opening Range Breakout (ORB)", "Mean Reversion", "News / Earnings", "Other"
];

export default function App() {
  // Navigation & Core States
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [calDate, setCalDate] = useState(new Date());

  // Multi-Account Engine
  const [accounts, setAccounts] = useState([{ id: 'default', name: 'Main Account', startingCapital: 10000 }]);
  const [activeAccountId, setActiveAccountId] = useState('default');
  const activeAccount = accounts.find(a => a.id === activeAccountId) || accounts[0];
  const startingCapital = activeAccount.startingCapital;

  const [trades, setTrades] = useState([]);
  
  // Isolated data stream for the current active account
  const activeTrades = useMemo(() => {
    return trades.filter(t => t.accountId === activeAccountId);
  }, [trades, activeAccountId]);

  // Account Settings Form
  const [accountForm, setAccountForm] = useState({ name: activeAccount.name, startingCapital: activeAccount.startingCapital });
  
  useEffect(() => {
    setAccountForm({ name: activeAccount.name, startingCapital: activeAccount.startingCapital });
  }, [activeAccountId, activeAccount.name, activeAccount.startingCapital]);

  // File Upload Ref for Importing Data
  const fileInputRef = useRef(null);

  // Risk Manager State
  const [riskSettings, setRiskSettings] = useState(() => {
    const saved = JSON.parse(localStorage.getItem('reactRiskSettings'));
    return saved || { riskPct: 2, stopLossPct: 20, targetPct: 50 };
  });

  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  
  // Ledger Filters
  const [ledgerAssetClass, setLedgerAssetClass] = useState('All');
  const [filterSetup, setFilterSetup] = useState('All Categories');

  const defaultForm = { 
    assetClass: 'Options', // Options, Stocks, Futures, Crypto
    date: '', entryTime: '09:30', ticker: '', strike: '', expiration: '',
    type: 'Call', // For Options
    direction: 'Long', // For Stocks/Futures/Crypto
    multiplier: 50, // For Futures
    setup: 'Breakout / Breakdown', qty: 1, entry: '', exits: [],
    entryEarly: false, entryLate: false, soldEarly: false, soldLate: false, commission: ''
  };
  const [formData, setFormData] = useState(defaultForm);

  // --- SORTING ENGINE & ICONS ---
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <span style={{opacity: 0.3}}>↕</span>;
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // --- INITIALIZATION & LOCAL STORAGE ---
  useEffect(() => {
    // 1. Load or Migrate Accounts
    const savedAccounts = JSON.parse(localStorage.getItem('reactAccounts'));
    if (savedAccounts && savedAccounts.length > 0) {
      setAccounts(savedAccounts);
      const savedActiveId = localStorage.getItem('reactActiveAccountId');
      if (savedActiveId && savedAccounts.find(a => a.id === savedActiveId)) {
        setActiveAccountId(savedActiveId);
      } else {
        setActiveAccountId(savedAccounts[0].id);
      }
    } else {
      const legacyName = localStorage.getItem('reactUsername') || 'Main Account';
      const legacyCap = Number(localStorage.getItem('reactStartingCapital')) || 10000;
      const defaultAcc = { id: 'default', name: legacyName, startingCapital: legacyCap };
      setAccounts([defaultAcc]);
      setActiveAccountId('default');
      localStorage.setItem('reactAccounts', JSON.stringify([defaultAcc]));
      localStorage.setItem('reactActiveAccountId', 'default');
    }

    // 2. Load Trades and assign Account ID + Asset Class if migrating
    const savedTrades = JSON.parse(localStorage.getItem('reactTrades')) || [];
    const migratedTrades = savedTrades.map(t => {
      const base = { 
        ...t, 
        accountId: t.accountId || 'default',
        assetClass: t.assetClass || 'Options',
        qty: t.qty || t.contracts || 1, 
        direction: t.direction || 'Long',
        entryTime: t.entryTime || '09:30', 
        entryEarly: t.entryEarly || false, entryLate: t.entryLate || false, 
        soldEarly: t.soldEarly || false, soldLate: t.soldLate || false,
        commission: t.commission || 0 
      };
      if (base.setup === 'Breakout') base.setup = 'Breakout / Breakdown';
      if (base.setup === 'Reversal') base.setup = 'Mean Reversion';
      
      if (base.exits) {
        base.exits = base.exits.map(ex => ({ ...ex, qty: ex.qty || ex.contracts || 1, time: ex.time || '16:00' }));
        return base;
      } 
      return { ...base, exits: (t.exit && t.exitDate) ? [{ date: t.exitDate, time: '16:00', price: t.exit, qty: t.qty || t.contracts || 1 }] : [] };
    });
    setTrades(migratedTrades);
    
    const now = new Date();
    setFormData(prev => ({ ...prev, date: now.toISOString().split('T')[0], entryTime: now.toTimeString().slice(0,5) }));
  }, []);

  // --- MULTI-ACCOUNT MANAGEMENT METHODS ---
  const handleUpdateAccount = (e) => {
    e.preventDefault();
    const updatedAccounts = accounts.map(a => 
      a.id === activeAccountId 
        ? { ...a, name: accountForm.name, startingCapital: accountForm.startingCapital } 
        : a
    );
    setAccounts(updatedAccounts);
    localStorage.setItem('reactAccounts', JSON.stringify(updatedAccounts));
    alert('Account settings updated successfully!');
  };

  const handleCreateAccount = () => {
    const name = prompt('Enter a nickname for the new account (e.g., Prop Firm Eval):');
    if (!name) return;
    const cap = prompt('Enter starting capital for this account:', '10000');
    if (!cap) return;
    
    const newAcc = { id: 'acc_' + Date.now(), name, startingCapital: Number(cap) };
    const updated = [...accounts, newAcc];
    setAccounts(updated);
    setActiveAccountId(newAcc.id);
    
    localStorage.setItem('reactAccounts', JSON.stringify(updated));
    localStorage.setItem('reactActiveAccountId', newAcc.id);
  };

  const handleDeleteAccount = () => {
    if (accounts.length <= 1) return alert('Cannot delete the last remaining account.');
    if (window.confirm(`Are you sure you want to delete "${activeAccount.name}" and ALL of its tracked trades? This cannot be undone.`)) {
       const updatedAccounts = accounts.filter(a => a.id !== activeAccountId);
       const updatedTrades = trades.filter(t => t.accountId !== activeAccountId);
       
       setAccounts(updatedAccounts);
       setTrades(updatedTrades);
       setActiveAccountId(updatedAccounts[0].id);
       
       localStorage.setItem('reactAccounts', JSON.stringify(updatedAccounts));
       localStorage.setItem('reactTrades', JSON.stringify(updatedTrades));
       localStorage.setItem('reactActiveAccountId', updatedAccounts[0].id);
    }
  };

  const updateRiskSettings = (key, value) => {
    const updated = { ...riskSettings, [key]: Number(value) };
    setRiskSettings(updated);
    localStorage.setItem('reactRiskSettings', JSON.stringify(updated));
  };

  const handleExportData = () => {
    const backupData = {
      accounts: accounts,
      activeAccountId: activeAccountId,
      trades: trades,
      riskSettings: riskSettings
    };
    
    const dataStr = JSON.stringify(backupData, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `trade_knight_backup_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportData = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const importedData = JSON.parse(event.target.result);
        
        if (importedData.accounts) {
          setAccounts(importedData.accounts);
          localStorage.setItem('reactAccounts', JSON.stringify(importedData.accounts));
        } else if (importedData.username) {
          const legacyAcc = { id: 'default', name: importedData.username, startingCapital: importedData.startingCapital || 10000 };
          setAccounts([legacyAcc]);
          localStorage.setItem('reactAccounts', JSON.stringify([legacyAcc]));
        }

        if (importedData.activeAccountId) {
          setActiveAccountId(importedData.activeAccountId);
          localStorage.setItem('reactActiveAccountId', importedData.activeAccountId);
        } else {
          setActiveAccountId('default');
          localStorage.setItem('reactActiveAccountId', 'default');
        }

        if (importedData.trades) {
          const importedT = importedData.trades.map(t => ({...t, accountId: t.accountId || 'default'}));
          setTrades(importedT);
          localStorage.setItem('reactTrades', JSON.stringify(importedT));
        }

        if (importedData.riskSettings) {
          setRiskSettings(importedData.riskSettings);
          localStorage.setItem('reactRiskSettings', JSON.stringify(importedData.riskSettings));
        }
        
        alert("Backup imported successfully!");
      } catch (err) {
        alert("Error importing file. Please ensure it is a valid Trade Knight JSON backup file.");
      }
      
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    reader.readAsText(file);
  };

  const formatMoney = (val) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
  };

  // --- CORE ANALYTICS ENGINE (ISOLATED BY ACTIVE ACCOUNT) ---
  const stats = useMemo(() => {
    if (activeTrades.length === 0) return { totalPnl: 0, winRate: 0, pf: 0, avgWin: 0, avgLoss: 0, best: 0, worst: 0, streak: '0', todayPnl: 0, todayCount: 0, openCount: 0, totalClosed: 0, maxWinStreak: 0, winCount: 0, lossCount: 0 };
    
    let totalPnl = 0, grossWin = 0, grossLoss = 0, winCount = 0, lossCount = 0;
    let best = -Infinity, worst = Infinity;
    let currentStreak = 0, maxWinStreak = 0, streakType = null;
    let todayPnl = 0, todayCount = 0, openCount = 0;
    
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    
    const sorted = [...activeTrades].sort((a, b) => new Date(a.date) - new Date(b.date));

    sorted.forEach(t => {
      let realizedPnl = 0;
      let exitedToday = false;
      
      t.exits.forEach(ex => {
        const exPnl = calcTradeExitPnl(t, ex);
        realizedPnl += exPnl;
        if (ex.date === todayStr) { todayPnl += exPnl; exitedToday = true; }
      });

      totalPnl += realizedPnl;
      if (exitedToday) todayCount++;

      if (t.status === 'Closed') {
        if (realizedPnl > best) best = realizedPnl;
        if (realizedPnl < worst) worst = realizedPnl;
        
        if (realizedPnl >= 0) {
          grossWin += realizedPnl; winCount++;
          if (streakType === 'W') currentStreak++; else { streakType = 'W'; currentStreak = 1; }
          if (currentStreak > maxWinStreak) maxWinStreak = currentStreak;
        } else {
          grossLoss += Math.abs(realizedPnl); lossCount++;
          if (streakType === 'L') currentStreak++; else { streakType = 'L'; currentStreak = 1; }
        }
      } else { openCount++; }
    });

    const totalClosed = winCount + lossCount;

    return {
      totalPnl,
      winRate: totalClosed > 0 ? ((winCount / totalClosed) * 100).toFixed(1) : 0,
      pf: grossLoss === 0 ? grossWin.toFixed(2) : (grossWin / grossLoss).toFixed(2),
      avgWin: winCount > 0 ? grossWin / winCount : 0,
      avgLoss: lossCount > 0 ? (grossLoss / lossCount) * -1 : 0,
      best: best !== -Infinity ? best : 0, worst: worst !== Infinity ? worst : 0,
      streak: `${currentStreak} ${streakType || ''}`, maxWinStreak,
      todayPnl, todayCount, openCount, totalClosed, winCount, lossCount
    };
  }, [activeTrades]);

  const charts = useMemo(() => {
    let cumPnl = startingCapital; 
    const dailyMap = {}; const setupMap = {}; 
    activeTrades.forEach(t => {
      t.exits.forEach(ex => {
        const exPnl = calcTradeExitPnl(t, ex);
        if (!dailyMap[ex.date]) dailyMap[ex.date] = 0;
        dailyMap[ex.date] += exPnl;
        const setupName = t.setup || 'Other';
        if (!setupMap[setupName]) setupMap[setupName] = 0;
        setupMap[setupName] += exPnl;
      });
    });
    const dates = Object.keys(dailyMap).sort((a, b) => new Date(a) - new Date(b));
    const equityLabels = ['Start', ...dates]; const equityData = [startingCapital]; const dailyData = [];
    dates.forEach(d => { cumPnl += dailyMap[d]; equityData.push(cumPnl); dailyData.push(dailyMap[d]); });

    return {
      equity: { labels: equityLabels, datasets: [{ label: 'Total Equity', data: equityData, borderColor: '#3b82f6', backgroundColor: 'rgba(59, 130, 246, 0.1)', fill: true, tension: 0.4 }] },
      daily: { labels: dates.length ? dates : ['No Data'], datasets: [{ label: 'Daily P&L', data: dailyData.length ? dailyData : [0], backgroundColor: (ctx) => ctx.raw >= 0 ? '#22c55e' : '#ef4444', borderRadius: 4 }] },
      edge: { 
        labels: Object.keys(setupMap).length ? Object.keys(setupMap) : ['No Data'], 
        datasets: [{ 
          label: 'Net P&L by Strategy', 
          data: Object.keys(setupMap).length ? Object.values(setupMap) : [0], 
          backgroundColor: 'rgba(139, 92, 246, 0.3)',
          borderColor: 'rgba(139, 92, 246, 1)',
          borderWidth: 1,
          pointBackgroundColor: 'rgba(139, 92, 246, 1)',
          pointBorderColor: '#fff'
        }] 
      }
    };
  }, [activeTrades, startingCapital]);

  const executionStats = useMemo(() => {
    const closedTrades = activeTrades.filter(t => t.status === 'Closed');
    const totalClosed = closedTrades.length;
    let entryEarly = 0, entryLate = 0, soldEarly = 0, soldLate = 0;

    closedTrades.forEach(t => {
       if (t.entryEarly) entryEarly++;
       if (t.entryLate) entryLate++;
       if (t.soldEarly) soldEarly++;
       if (t.soldLate) soldLate++;
    });

    return {
       totalClosed,
       entryEarly, entryLate, soldEarly, soldLate,
       entryEarlyPct: totalClosed ? ((entryEarly / totalClosed) * 100).toFixed(1) : 0,
       entryLatePct: totalClosed ? ((entryLate / totalClosed) * 100).toFixed(1) : 0,
       soldEarlyPct: totalClosed ? ((soldEarly / totalClosed) * 100).toFixed(1) : 0,
       soldLatePct: totalClosed ? ((soldLate / totalClosed) * 100).toFixed(1) : 0
    };
  }, [activeTrades]);

  const executionChartData = {
     labels: ['Entry Too Early', 'Entry Too Late', 'Sold Too Early', 'Sold Too Late'],
     datasets: [{
        label: '# of Trades',
        data: [executionStats.entryEarly, executionStats.entryLate, executionStats.soldEarly, executionStats.soldLate],
        backgroundColor: 'rgba(139, 92, 246, 0.3)',
        borderColor: 'rgba(139, 92, 246, 1)',
        borderWidth: 1,
        pointBackgroundColor: 'rgba(139, 92, 246, 1)',
        pointBorderColor: '#fff',
     }]
  };

  const chartOptions = {
    responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } },
    scales: { x: { grid: { display: false } }, y: { border: { display: false }, ticks: { callback: function(value) { return '$' + value.toLocaleString(); } } } }
  };

  const analyticsRadarOptions = {
    responsive: true, 
    maintainAspectRatio: false, 
    plugins: { legend: { display: false } },
    scales: { 
      r: { 
        angleLines: { color: 'rgba(255,255,255,0.1)' }, 
        grid: { color: 'rgba(255,255,255,0.1)' }, 
        pointLabels: { color: '#9ca3af', font: { size: 11 } }, 
        ticks: { display: false } 
      } 
    }
  };

  const executionRadarOptions = {
    ...analyticsRadarOptions,
    scales: {
      r: {
        ...analyticsRadarOptions.scales.r,
        ticks: { display: false, min: 0 } // Anchors the center at 0 for realistic frequency spread
      }
    }
  };

  const processedTrades = useMemo(() => {
    let filtered = activeTrades;
    if (ledgerAssetClass !== 'All') filtered = filtered.filter(t => (t.assetClass || 'Options') === ledgerAssetClass);
    if (filterSetup !== 'All Categories') filtered = filtered.filter(t => t.setup === filterSetup);
    
    let sortable = [...filtered];
    sortable.sort((a, b) => {
      if (sortConfig.key === 'date') {
        const dateA = new Date(`${a.date}T${a.entryTime}`);
        const dateB = new Date(`${b.date}T${b.entryTime}`);
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      if (sortConfig.key === 'pnl') {
        return sortConfig.direction === 'asc' ? a.pnl - b.pnl : b.pnl - a.pnl;
      }
      if (['ticker', 'setup', 'status'].includes(sortConfig.key)) {
        const valA = (a[sortConfig.key] || '').toLowerCase();
        const valB = (b[sortConfig.key] || '').toLowerCase();
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      }
      return 0;
    });
    return sortable;
  }, [activeTrades, sortConfig, filterSetup, ledgerAssetClass]);

  const openLogModal = () => {
    setEditingId(null);
    const now = new Date();
    setFormData({ ...defaultForm, date: now.toISOString().split('T')[0], entryTime: now.toTimeString().slice(0,5) });
    setIsModalOpen(true);
  };

  const handleEditClick = (trade) => {
    setEditingId(trade.id);
    setFormData({
      assetClass: trade.assetClass || 'Options',
      date: trade.date || '', entryTime: trade.entryTime || '09:30', ticker: trade.ticker || '', 
      strike: trade.strike || '', expiration: trade.expiration || '', 
      type: trade.type || 'Call', direction: trade.direction || 'Long', multiplier: trade.multiplier || 50,
      setup: trade.setup || 'Breakout / Breakdown', qty: trade.qty || trade.contracts || 1, 
      entry: trade.entry || '', exits: trade.exits ? [...trade.exits] : [],
      entryEarly: trade.entryEarly || false, entryLate: trade.entryLate || false,
      soldEarly: trade.soldEarly || false, soldLate: trade.soldLate || false,
      commission: trade.commission || ''
    });
    setIsModalOpen(true);
  };

  const addExit = () => {
    const totalExited = formData.exits.reduce((sum, e) => sum + Number(e.qty || e.contracts || 0), 0);
    const remaining = Math.max(1, formData.qty - totalExited);
    const now = new Date();
    setFormData({ ...formData, exits: [...formData.exits, { date: now.toISOString().split('T')[0], time: now.toTimeString().slice(0,5), price: '', qty: remaining }] });
  };
  const updateExit = (index, field, value) => { const newExits = [...formData.exits]; newExits[index][field] = value; setFormData({ ...formData, exits: newExits }); };
  const removeExit = (index) => { const newExits = [...formData.exits]; newExits.splice(index, 1); setFormData({ ...formData, exits: newExits }); };

  const handleSaveTrade = (e) => {
    e.preventDefault();
    const totalExited = formData.exits.reduce((sum, ex) => sum + Number(ex.qty || ex.contracts), 0);
    if (totalExited > formData.qty) { alert(`Error: Trying to exit ${totalExited} qty, but only entered with ${formData.qty}.`); return; }

    const pnl = formData.exits.reduce((sum, ex) => sum + calcTradeExitPnl(formData, ex), 0);
    const status = totalExited >= formData.qty ? 'Closed' : 'Open';
    
    const tradeData = { 
      id: editingId || Date.now(), 
      accountId: activeAccountId, 
      ...formData, 
      entry: parseFloat(formData.entry), 
      strike: formData.assetClass === 'Options' ? parseFloat(formData.strike) : null, 
      commission: Number(formData.commission || 0),
      status, 
      pnl 
    };
    
    const updatedTrades = editingId ? trades.map(t => t.id === editingId ? tradeData : t) : [...trades, tradeData];
    setTrades(updatedTrades); 
    localStorage.setItem('reactTrades', JSON.stringify(updatedTrades));
    setIsModalOpen(false); setEditingId(null);
  };

  const deleteTrade = (id) => { 
    if(window.confirm("Delete this trade?")) { 
      const updated = trades.filter(t => t.id !== id); 
      setTrades(updated); 
      localStorage.setItem('reactTrades', JSON.stringify(updated)); 
    } 
  };
  
  const factoryReset = () => { 
    if(window.confirm("DANGER: Permanently delete ALL trades from ALL accounts?")) { 
      setTrades([]); 
      localStorage.removeItem('reactTrades'); 
    } 
  };
  const changeMonth = (offset) => setCalDate(new Date(calDate.getFullYear(), calDate.getMonth() + offset, 1));
  
  // --- ZELLA & CALENDAR MATH ENGINE ---
  const monthData = useMemo(() => {
    const year = calDate.getFullYear();
    const month = calDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const dailyStats = {};
    for(let i=1; i<=daysInMonth; i++) {
      const dStr = `${year}-${String(month+1).padStart(2,'0')}-${String(i).padStart(2,'0')}`;
      dailyStats[dStr] = { pnl: 0, wins: 0, losses: 0, bes: 0, trades: 0 };
    }

    let mPnl = 0, mGrossWin = 0, mGrossLoss = 0, mWins = 0, mLosses = 0, mBes = 0, mTrades = 0, activeDays = 0;

    activeTrades.forEach(t => {
       t.exits.forEach(ex => {
          const [y, m] = ex.date.split('-');
          if (parseInt(y) === year && parseInt(m) - 1 === month) {
             const exPnl = calcTradeExitPnl(t, ex);
             if (dailyStats[ex.date]) dailyStats[ex.date].pnl += exPnl;
             mPnl += exPnl;
          }
       });

       if (t.status === 'Closed' && t.exits.length > 0) {
          const lastExit = t.exits[t.exits.length - 1];
          const [y, m] = lastExit.date.split('-');
          if (parseInt(y) === year && parseInt(m) - 1 === month) {
             if (dailyStats[lastExit.date]) dailyStats[lastExit.date].trades += 1;
             mTrades += 1;

             if (t.pnl > 0) {
                if (dailyStats[lastExit.date]) dailyStats[lastExit.date].wins += 1;
                mWins++; mGrossWin += t.pnl;
             } else if (t.pnl < 0) {
                if (dailyStats[lastExit.date]) dailyStats[lastExit.date].losses += 1;
                mLosses++; mGrossLoss += Math.abs(t.pnl);
             } else {
                if (dailyStats[lastExit.date]) dailyStats[lastExit.date].bes += 1;
                mBes++;
             }
          }
       }
    });

    Object.values(dailyStats).forEach(ds => {
       if (ds.trades > 0 || ds.pnl !== 0) activeDays++;
    });

    const winRate = mTrades > 0 ? (mWins / mTrades) * 100 : 0;
    const profitFactor = mGrossLoss === 0 ? (mGrossWin > 0 ? 99 : 0) : mGrossWin / mGrossLoss;
    const avgWin = mWins > 0 ? mGrossWin / mWins : 0;
    const avgLoss = mLosses > 0 ? mGrossLoss / mLosses : 0;
    const avgWinLossRatio = avgLoss > 0 ? avgWin / avgLoss : (avgWin > 0 ? 99 : 0);

    let cum = 0, maxDrawdown = 0, peak = 0;
    const cumPnlData = []; const cumPnlLabels = [];
    Object.keys(dailyStats).sort().forEach(date => {
       cum += dailyStats[date].pnl;
       cumPnlData.push(cum);
       cumPnlLabels.push(date);
       if (cum > peak) peak = cum;
       const dd = peak - cum;
       if (dd > maxDrawdown) maxDrawdown = dd;
    });

    const scoreWinPct = winRate;
    const scorePF = Math.min((profitFactor / 3) * 100, 100);
    const scoreAWL = Math.min((avgWinLossRatio / 3) * 100, 100);
    const scoreConsistency = Math.min((activeDays / 15) * 100, 100);
    const scoreMDD = Math.max(100 - (maxDrawdown / (startingCapital * 0.1) * 100), 0);
    const recoveryFactor = maxDrawdown > 0 ? (mPnl / maxDrawdown) : (mPnl > 0 ? 5 : 0);
    const scoreRec = Math.min((recoveryFactor / 5) * 100, 100);

    const knightScore = Math.round((scoreWinPct + scorePF + scoreAWL + scoreConsistency + scoreMDD + scoreRec) / 6) || 0;

    return {
      dailyStats, mPnl, mTrades, mWins, mLosses, mBes, activeDays, winRate, profitFactor, avgWin, avgLoss, avgWinLossRatio,
      cumPnlData, cumPnlLabels, knightScore, scoreWinPct, scorePF, scoreAWL, scoreConsistency, scoreMDD, scoreRec
    };
  }, [activeTrades, calDate, startingCapital]);

  const renderCalendar = () => {
    const year = calDate.getFullYear();
    const month = calDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const weeks = [];
    let currentWeek = Array(firstDay).fill(null);
    for (let day = 1; day <= daysInMonth; day++) {
      currentWeek.push(day);
      if (currentWeek.length === 7) { weeks.push(currentWeek); currentWeek = []; }
    }
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) currentWeek.push(null);
      weeks.push(currentWeek);
    }

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: '8px', marginTop: '16px' }}>
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat', 'Wk P&L'].map(d => (
          <div key={d} style={{ textAlign:'center', color: d === 'Wk P&L' ? 'var(--text-main)' : 'var(--text-muted)', fontSize: '12px', fontWeight: 'bold' }}>{d}</div>
        ))}
        {weeks.map((week, wIndex) => {
          let weeklyTotal = 0; let hasTradesThisWeek = false;
          
          const weekCells = week.map((day, dIndex) => {
            if (day === null) return <div key={`blank-${wIndex}-${dIndex}`} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '8px' }} />;
            
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayData = monthData.dailyStats[dateStr];
            const pnl = dayData?.pnl || 0;
            const tradesCount = dayData?.trades || 0;
            const isPositive = pnl >= 0;

            if (tradesCount > 0 || pnl !== 0) {
              weeklyTotal += pnl;
              hasTradesThisWeek = true;
            }

            const hasData = tradesCount > 0 || pnl !== 0;
            
            return (
              <div key={`day-${day}`} style={{ background: hasData ? (isPositive ? 'rgba(34, 197, 94, 0.05)' : 'rgba(239, 68, 68, 0.05)') : 'var(--card-bg)', border: hasData ? (isPositive ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid rgba(239, 68, 68, 0.4)') : '1px solid var(--border)', borderRadius: '8px', padding: '8px', height: '100px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ textAlign: 'right', color: 'var(--text-muted)', fontSize: '12px' }}>{day}</div>
                {hasData && ( 
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                    <div className={isPositive ? 'text-green' : 'text-red'} style={{ fontWeight: 'bold', fontSize: '14px' }}>
                      {isPositive ? '+' : ''}{formatMoney(pnl)}
                    </div>
                    <div style={{fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px'}}>{tradesCount} {tradesCount === 1 ? 'trade' : 'trades'}</div>
                  </div> 
                )}
              </div>
            );
          });

          const isWeekPositive = weeklyTotal >= 0;
          const weeklyCard = (
            <div key={`weekly-${wIndex}`} style={{ background: 'rgba(255,255,255,0.03)', border: '1px dashed var(--border)', borderRadius: '8px', padding: '12px', minHeight: '80px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
               {hasTradesThisWeek ? (
                 <><div style={{fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '4px'}}>Net P&L</div><div className={isWeekPositive ? 'text-green' : 'text-red'} style={{ fontWeight: 'bold', fontSize: '15px' }}>{isWeekPositive ? '+' : ''}{formatMoney(weeklyTotal)}</div></>
               ) : (<div style={{color: 'var(--text-muted)', fontSize: '12px'}}>-</div>)}
            </div>
          );
          return [...weekCells, weeklyCard];
        })}
      </div>
    );
  };

  const currentEquity = startingCapital + stats.totalPnl;
  const dollarRisk = currentEquity * (riskSettings.riskPct / 100);
  const positionSize = riskSettings.stopLossPct > 0 ? dollarRisk / (riskSettings.stopLossPct / 100) : 0;
  const targetGain = positionSize * (riskSettings.targetPct / 100);

  return (
    <div className="app-container" style={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
      {/* SIDEBAR */}
      <nav className="sidebar" style={{ width: isSidebarCollapsed ? '80px' : '260px', transition: 'width 0.3s ease', display: 'flex', flexDirection: 'column', overflowX: 'hidden', flexShrink: 0 }}>
        <div className="sidebar-header" style={{ justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', padding: isSidebarCollapsed ? '20px 0' : '20px' }}>
          <div className="logo-icon">TK</div>
          {!isSidebarCollapsed && (
            <div style={{ marginLeft: '12px' }}>
              <div style={{fontWeight: 'bold', fontSize: '16px', letterSpacing: '1px'}}>TRADE KNIGHT</div>
              <div style={{fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase'}}>Track Your Edge</div>
            </div>
          )}
        </div>
        <div className="nav-menu" style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '0' }}>
          <button className={`nav-btn ${activeTab === 'dashboard' ? 'active' : ''}`} style={{ display: 'flex', alignItems: 'center', justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', padding: isSidebarCollapsed ? '16px 0' : '16px 20px', whiteSpace: 'nowrap' }} onClick={() => setActiveTab('dashboard')}>
            <Icon name="dashboard" /> {!isSidebarCollapsed && <span style={{ marginLeft: '12px' }}>Dashboard</span>}
          </button>
          <button className={`nav-btn ${activeTab === 'trades' ? 'active' : ''}`} style={{ display: 'flex', alignItems: 'center', justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', padding: isSidebarCollapsed ? '16px 0' : '16px 20px', whiteSpace: 'nowrap' }} onClick={() => setActiveTab('trades')}>
            <Icon name="trades" /> {!isSidebarCollapsed && <span style={{ marginLeft: '12px' }}>Trades</span>}
          </button>
          <button className={`nav-btn ${activeTab === 'risk' ? 'active' : ''}`} style={{ display: 'flex', alignItems: 'center', justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', padding: isSidebarCollapsed ? '16px 0' : '16px 20px', whiteSpace: 'nowrap' }} onClick={() => setActiveTab('risk')}>
            <Icon name="risk" /> {!isSidebarCollapsed && <span style={{ marginLeft: '12px' }}>Risk Sizer</span>}
          </button>
          <button className={`nav-btn ${activeTab === 'calendar' ? 'active' : ''}`} style={{ display: 'flex', alignItems: 'center', justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', padding: isSidebarCollapsed ? '16px 0' : '16px 20px', whiteSpace: 'nowrap' }} onClick={() => setActiveTab('calendar')}>
            <Icon name="calendar" /> {!isSidebarCollapsed && <span style={{ marginLeft: '12px' }}>Calendar</span>}
          </button>
          <button className={`nav-btn ${activeTab === 'analytics' ? 'active' : ''}`} style={{ display: 'flex', alignItems: 'center', justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', padding: isSidebarCollapsed ? '16px 0' : '16px 20px', whiteSpace: 'nowrap' }} onClick={() => setActiveTab('analytics')}>
            <Icon name="analytics" /> {!isSidebarCollapsed && <span style={{ marginLeft: '12px' }}>Analytics</span>}
          </button>
          <button className={`nav-btn ${activeTab === 'playbook' ? 'active' : ''}`} style={{ display: 'flex', alignItems: 'center', justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', padding: isSidebarCollapsed ? '16px 0' : '16px 20px', whiteSpace: 'nowrap' }} onClick={() => setActiveTab('playbook')}>
            <Icon name="playbook" /> {!isSidebarCollapsed && <span style={{ marginLeft: '12px' }}>Playbook</span>}
          </button>
          <button className={`nav-btn ${activeTab === 'settings' ? 'active' : ''}`} style={{ display: 'flex', alignItems: 'center', justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', padding: isSidebarCollapsed ? '16px 0' : '16px 20px', whiteSpace: 'nowrap' }} onClick={() => setActiveTab('settings')}>
            <Icon name="settings" /> {!isSidebarCollapsed && <span style={{ marginLeft: '12px' }}>Settings</span>}
          </button>

          <div style={{ flexGrow: 1 }}></div>

          <button 
            className="nav-btn" 
            style={{ display: 'flex', alignItems: 'center', justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', padding: isSidebarCollapsed ? '16px 0' : '16px 20px', whiteSpace: 'nowrap', color: 'var(--primary)' }} 
            onClick={() => window.open('https://buymeacoffee.com/zen.knight', '_blank')}
          >
            <Icon name="coffee" /> {!isSidebarCollapsed && <span style={{ marginLeft: '12px' }}>Donate</span>}
          </button>

          <button onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)} className="nav-btn" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px 0', borderTop: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)' }}>
            <Icon name={isSidebarCollapsed ? "chevronRight" : "chevronLeft"} />
            {!isSidebarCollapsed && <span style={{ marginLeft: '12px' }}>Collapse Menu</span>}
          </button>
        </div>
      </nav>

      {/* MAIN CONTENT */}
      <main className="main-content" style={{ flex: 1, minWidth: 0, transition: 'all 0.3s ease' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <select 
              value={activeAccountId}
              onChange={(e) => {
                setActiveAccountId(e.target.value);
                localStorage.setItem('reactActiveAccountId', e.target.value);
              }}
              style={{
                fontSize: '24px', 
                fontWeight: '900', 
                background: 'transparent', 
                color: 'var(--text-main)', 
                border: 'none', 
                outline: 'none', 
                cursor: 'pointer', 
                textTransform: 'uppercase',
                letterSpacing: '1px',
                borderBottom: '2px solid var(--primary)',
                paddingRight: '4px'
              }}
            >
              {accounts.map(acc => (
                <option key={acc.id} value={acc.id} style={{ background: 'var(--card-bg)', color: 'var(--text-main)', textTransform: 'none', fontSize: '16px' }}>
                  {acc.name}
                </option>
              ))}
            </select>
          </div>
          
          <div style={{display: 'flex', gap: '12px'}}>
            <button onClick={handleExportData} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-main)', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
              📥 Export Backup
            </button>
            <button className="log-trade-btn" onClick={openLogModal}>+ LOG TRADE</button>
          </div>
        </div>

        {activeTab === 'dashboard' && (
          <div className="dashboard-grid">
            <div className="top-row">
              <div className="card">
                <div className="card-title">Realized Today</div>
                <div className={`hero-value ${stats.todayPnl >= 0 ? 'text-green' : 'text-red'}`}>{stats.todayPnl >= 0 ? '+' : ''}{formatMoney(stats.todayPnl)}</div>
                <div style={{color: 'var(--text-muted)', fontSize: '14px', marginTop: '8px'}}>{stats.todayCount} trades closed today</div>
              </div>
              <div className="card">
                <div className="card-title">Closed Trade Stats</div>
                <div style={{display: 'flex', flexDirection: 'column', gap: '12px', fontWeight: 'bold'}}>
                  <div>Best: <span className={stats.best >= 0 ? 'text-green' : 'text-red'}>{formatMoney(stats.best)}</span></div>
                  <div>Worst: <span className={stats.worst >= 0 ? 'text-green' : 'text-red'}>{formatMoney(stats.worst)}</span></div>
                  <div>Current Streak: <span className={stats.streak.includes('W') ? 'text-green' : 'text-red'}>{stats.streak}</span></div>
                </div>
              </div>
              <div className="card" style={{border: '1px solid var(--primary)', backgroundColor: 'rgba(163, 230, 53, 0.05)'}}>
                <div className="card-title" style={{color: 'var(--text-main)'}}>Current Equity</div>
                <div className="hero-value" style={{color: 'var(--primary)'}}>{formatMoney(currentEquity)}</div>
                <div style={{color: 'var(--text-muted)', fontSize: '14px', marginTop: '8px'}}>Starting Capital: {formatMoney(startingCapital)}</div>
              </div>
            </div>

            <div className="stats-row">
              <div className="card"><div className="card-title">Total Realized P&L</div><div className={`stat-value ${stats.totalPnl >= 0 ? 'text-green' : 'text-red'}`}>{formatMoney(stats.totalPnl)}</div><div style={{fontSize: '12px', marginTop: '4px', color: 'var(--text-muted)'}}>ROI: {((stats.totalPnl / startingCapital) * 100).toFixed(2)}%</div></div>
              <div className="card"><div className="card-title">Win Rate</div><div className="stat-value">{stats.winRate}%</div></div>
              <div className="card"><div className="card-title">Profit Factor</div><div className="stat-value">{stats.pf}</div></div>
              <div className="card"><div className="card-title">Avg Winner</div><div className="stat-value text-green">{formatMoney(stats.avgWin)}</div></div>
              <div className="card"><div className="card-title">Open Positions</div><div className="stat-value text-muted" style={{color: '#ff9800'}}>{stats.openCount}</div></div>
            </div>

            <div className="charts-row">
              <div className="card" style={{height: '300px'}}><div className="card-title">Total Account Equity</div><Line data={charts.equity} options={chartOptions} /></div>
              <div className="card" style={{height: '300px'}}><div className="card-title">Daily P&L (Realized)</div><Bar data={charts.daily} options={chartOptions} /></div>
            </div>
          </div>
        )}

        {activeTab === 'risk' && (
          <div>
            <h2 style={{marginTop: 0}}>Risk Manager & Position Sizer</h2>
            <p style={{color: 'var(--text-muted)', marginBottom: '24px'}}>Calculate your exact options position size to ensure you never exceed your account risk tolerance.</p>

            <div className="dashboard-grid">
              <div className="top-row">
                 <div className="card" style={{border: '1px solid var(--primary)'}}>
                   <div className="card-title">Max Position Size</div>
                   <div className="hero-value" style={{color: 'var(--primary)'}}>{formatMoney(positionSize)}</div>
                   <div style={{color: 'var(--text-muted)', fontSize: '14px', marginTop: '8px'}}>Capital allowed for next trade</div>
                 </div>
                 <div className="card">
                   <div className="card-title">Absolute Dollar Risk</div>
                   <div className="hero-value text-red">{formatMoney(dollarRisk)}</div>
                   <div style={{color: 'var(--text-muted)', fontSize: '14px', marginTop: '8px'}}>If stopped out at {riskSettings.stopLossPct}%</div>
                 </div>
                 <div className="card">
                   <div className="card-title">First Target Gain</div>
                   <div className="hero-value text-green">{formatMoney(targetGain)}</div>
                   <div style={{color: 'var(--text-muted)', fontSize: '14px', marginTop: '8px'}}>At {riskSettings.targetPct}% profit</div>
                 </div>
              </div>

              <div className="card" style={{maxWidth: '800px'}}>
                <h3 style={{marginTop: 0}}>Trade Parameters</h3>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px'}}>
                   <div className="form-group">
                     <label>Account Risk per Trade (%)</label>
                     <input type="number" step="0.1" min="0.1" className="form-control" value={riskSettings.riskPct} onChange={e => updateRiskSettings('riskPct', e.target.value)} />
                   </div>
                   <div className="form-group">
                     <label>Options Contract Stop Loss (%)</label>
                     <input type="number" step="0.1" min="1" className="form-control" value={riskSettings.stopLossPct} onChange={e => updateRiskSettings('stopLossPct', e.target.value)} />
                   </div>
                   <div className="form-group">
                     <label>First Exit Target (%)</label>
                     <input type="number" step="0.1" min="1" className="form-control" value={riskSettings.targetPct} onChange={e => updateRiskSettings('targetPct', e.target.value)} />
                   </div>
                </div>
                <div style={{marginTop: '16px', padding: '16px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', fontSize: '14px', color: 'var(--text-muted)', lineHeight: '1.6'}}>
                  <strong>The Math:</strong> Based on your current equity of <strong>{formatMoney(currentEquity)}</strong>, a {riskSettings.riskPct}% risk means you are willing to lose exactly <strong>{formatMoney(dollarRisk)}</strong> on a single setup. Because you plan to cut the trade if the contract loses {riskSettings.stopLossPct}% of its value, you can safely allocate a total of <strong>{formatMoney(positionSize)}</strong> to the position.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* --- CALENDAR TAB WITH ZELLA UI --- */}
        {activeTab === 'calendar' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
              <div className="card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                 <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <div style={{fontSize: '12px', color: 'var(--text-muted)', fontWeight: 'bold'}}>Net P&L</div>
                    <div style={{fontSize: '12px', color: 'var(--primary)', fontWeight: 'bold', background: 'rgba(139, 92, 246, 0.1)', padding: '2px 8px', borderRadius: '10px'}}>{monthData.mTrades} trades</div>
                 </div>
                 <div className={monthData.mPnl >= 0 ? 'text-green' : 'text-red'} style={{fontSize: '32px', fontWeight: 'bold', marginTop: '12px'}}>
                    {monthData.mPnl >= 0 ? '+' : ''}{formatMoney(monthData.mPnl)}
                 </div>
              </div>

              <div className="card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                 <div style={{fontSize: '12px', color: 'var(--text-muted)', fontWeight: 'bold'}}>Profit factor</div>
                 <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px'}}>
                    <div style={{fontSize: '32px', fontWeight: 'bold', color: 'var(--text-main)'}}>{monthData.profitFactor.toFixed(2)}</div>
                    <div style={{width: '50px', height: '50px', position: 'relative'}}>
                       <Doughnut 
                         data={{ datasets: [{ data: [monthData.profitFactor, Math.max(0, 4 - monthData.profitFactor)], backgroundColor: ['#10b981', '#374151'], borderWidth: 0 }] }} 
                         options={{ maintainAspectRatio: true, cutout: '80%', plugins: { tooltip: { enabled: false } } }}
                       />
                    </div>
                 </div>
              </div>

              <div className="card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                 <div style={{fontSize: '12px', color: 'var(--text-muted)', fontWeight: 'bold'}}>Trade win %</div>
                 <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px'}}>
                    <div style={{fontSize: '32px', fontWeight: 'bold', color: 'var(--text-main)'}}>{monthData.winRate.toFixed(2)}%</div>
                    <div style={{width: '70px', position: 'relative'}}>
                       <Doughnut 
                         data={{ datasets: [{ data: [monthData.mWins, monthData.mBes, monthData.mLosses], backgroundColor: ['#10b981', '#3b82f6', '#ef4444'], borderWidth: 0, circumference: 180, rotation: -90 }] }} 
                         options={{ maintainAspectRatio: true, cutout: '80%', plugins: { tooltip: { enabled: false } } }}
                       />
                       <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '9px', marginTop: '-10px', fontWeight: 'bold'}}>
                          <span style={{color: '#10b981'}}>{monthData.mWins}</span>
                          <span style={{color: '#3b82f6'}}>{monthData.mBes}</span>
                          <span style={{color: '#ef4444'}}>{monthData.mLosses}</span>
                       </div>
                    </div>
                 </div>
              </div>

              <div className="card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                 <div style={{fontSize: '12px', color: 'var(--text-muted)', fontWeight: 'bold'}}>Avg win/loss trade</div>
                 <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px'}}>
                    <div style={{fontSize: '32px', fontWeight: 'bold', color: 'var(--text-main)'}}>{monthData.avgWinLossRatio.toFixed(2)}</div>
                    <div style={{width: '100px', display: 'flex', flexDirection: 'column'}}>
                       <div style={{display: 'flex', height: '6px', borderRadius: '3px', overflow: 'hidden'}}>
                          <div style={{flex: monthData.avgWin || 1, background: '#10b981'}}></div>
                          <div style={{flex: monthData.avgLoss || 1, background: '#ef4444'}}></div>
                       </div>
                       <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '10px', marginTop: '4px', fontWeight: 'bold'}}>
                          <span style={{color: '#10b981'}}>{`$${Math.round(monthData.avgWin)}`}</span>
                          <span style={{color: '#ef4444'}}>-{`$${Math.round(monthData.avgLoss)}`}</span>
                       </div>
                    </div>
                 </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '24px', alignItems: 'start' }}>
               <div className="card" style={{ padding: '24px' }}>
                 <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
                      <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                        <button onClick={() => changeMonth(-1)} style={{background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px'}}>◀</button>
                        <h3 style={{margin: 0, color: 'var(--text-main)', fontSize: '18px'}}>{calDate.toLocaleString('default', { month: 'long', year: 'numeric' })}</h3>
                        <button onClick={() => changeMonth(1)} style={{background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px'}}>▶</button>
                      </div>
                      <button onClick={() => setCalDate(new Date())} style={{background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-main)', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer'}}>This month</button>
                    </div>
                    <div style={{fontSize: '13px', color: 'var(--text-main)', fontWeight: 'bold'}}>
                      Monthly stats: <span style={{background: 'rgba(16, 185, 129, 0.2)', color: '#10b981', padding: '2px 8px', borderRadius: '4px', marginLeft: '8px'}}>{monthData.mPnl >= 0 ? '+' : ''}{formatMoney(monthData.mPnl)}</span> <span style={{background: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6', padding: '2px 8px', borderRadius: '4px', marginLeft: '4px'}}>{monthData.activeDays} days</span>
                    </div>
                 </div>
                 {renderCalendar()}
               </div>

               <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  <div className="card">
                     <div style={{fontSize: '14px', fontWeight: 'bold', color: 'var(--text-main)', marginBottom: '16px'}}>Trade Knight Score</div>
                     <div style={{height: '200px'}}>
                        <Radar 
                          data={{
                            labels: ['Win %', 'Profit factor', 'Avg win/loss', 'Recovery factor', 'Max drawdown', 'Consistency'],
                            datasets: [{
                              data: [monthData.scoreWinPct, monthData.scorePF, monthData.scoreAWL, monthData.scoreRec, monthData.scoreMDD, monthData.scoreConsistency],
                              backgroundColor: 'rgba(139, 92, 246, 0.3)',
                              borderColor: 'rgba(139, 92, 246, 1)',
                              borderWidth: 1,
                              pointBackgroundColor: 'rgba(139, 92, 246, 1)',
                              pointBorderColor: '#fff',
                            }]
                          }}
                          options={{
                            scales: { r: { angleLines: { color: 'rgba(255,255,255,0.1)' }, grid: { color: 'rgba(255,255,255,0.1)' }, pointLabels: { color: '#9ca3af', font: { size: 10 } }, ticks: { display: false, min: 0, max: 100 } } },
                            plugins: { legend: { display: false } },
                            maintainAspectRatio: false
                          }}
                        />
                     </div>
                     <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '16px'}}>
                        <div>
                           <div style={{fontSize: '11px', color: 'var(--text-muted)'}}>Your Score</div>
                           <div style={{fontSize: '24px', fontWeight: 'bold', color: 'var(--text-main)'}}>{monthData.knightScore}</div>
                        </div>
                        <div style={{width: '150px', height: '6px', background: 'linear-gradient(90deg, #ef4444 0%, #eab308 50%, #10b981 100%)', borderRadius: '3px', position: 'relative'}}>
                           <div style={{position: 'absolute', top: '-4px', bottom: '-4px', width: '4px', background: '#fff', left: `${monthData.knightScore}%`, borderRadius: '2px'}}></div>
                        </div>
                     </div>
                  </div>

                  <div className="card">
                     <div style={{fontSize: '14px', fontWeight: 'bold', color: 'var(--text-main)', marginBottom: '16px'}}>Daily net cumulative P&L</div>
                     <div style={{height: '180px'}}>
                        <Line 
                          data={{ 
                            labels: monthData.cumPnlLabels.length ? monthData.cumPnlLabels : ['No Data'], 
                            datasets: [{ 
                              data: monthData.cumPnlData.length ? monthData.cumPnlData : [0], 
                              borderColor: '#10b981', 
                              backgroundColor: 'rgba(16, 185, 129, 0.1)', 
                              fill: true, 
                              tension: 0.4,
                              pointRadius: 0
                            }] 
                          }} 
                          options={chartOptions} 
                        />
                     </div>
                  </div>
               </div>
            </div>
          </div>
        )}

        {/* --- TRADES LEDGER TAB --- */}
        {activeTab === 'trades' && (
           <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
             <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
               <div className="card" style={{ padding: '20px' }}><div className="card-title">Total Trades</div><div className="stat-value">{activeTrades.length} <span style={{fontSize:'14px', color:'var(--text-muted)', fontWeight:'normal'}}>({stats.openCount} Open)</span></div></div>
               <div className="card" style={{ padding: '20px' }}><div className="card-title">Win Rate (Closed)</div><div className="stat-value">{stats.winRate}%</div></div>
               <div className="card" style={{ padding: '20px' }}><div className="card-title">Total Realized P&L</div><div className={`stat-value ${stats.totalPnl >= 0 ? 'text-green' : 'text-red'}`}>{formatMoney(stats.totalPnl)}</div></div>
             </div>
             
             <div className="card" style={{ overflowX: 'auto' }}>
               <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px'}}>
                 <h2 style={{margin: 0}}>Trade Ledger</h2>
                 <div style={{display: 'flex', gap: '12px'}}>
                   
                   <div style={{display: 'flex', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '4px'}}>
                     {['All', 'Options', 'Stocks', 'Futures', 'Crypto'].map(ac => (
                       <button 
                         key={ac} type="button" 
                         onClick={() => setLedgerAssetClass(ac)}
                         style={{
                           background: ledgerAssetClass === ac ? 'var(--primary)' : 'transparent',
                           color: ledgerAssetClass === ac ? '#000' : 'var(--text-muted)',
                           border: 'none', padding: '6px 12px', borderRadius: '6px', 
                           cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', transition: 'all 0.2s'
                         }}
                       >
                         {ac}
                       </button>
                     ))}
                   </div>

                   <select 
                      style={{padding: '8px 12px', background: 'var(--bg-color)', color: 'var(--text-main)', border: '1px solid var(--border)', borderRadius: '6px', outline: 'none'}}
                      value={filterSetup}
                      onChange={(e) => setFilterSetup(e.target.value)}
                   >
                     <option value="All Categories">All Strategies</option>
                     {SETUP_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                   </select>
                 </div>
               </div>

               <table style={{width: '100%', textAlign: 'center', borderCollapse: 'collapse'}}>
                 <thead>
                   <tr style={{borderBottom: '1px solid var(--border)'}}>
                     <th onClick={() => requestSort('date')} style={{padding: '12px', cursor: 'pointer', userSelect: 'none', textAlign: 'left'}}>Date {getSortIcon('date')}</th>
                     <th onClick={() => requestSort('ticker')} style={{padding: '12px', cursor: 'pointer', userSelect: 'none'}}>Ticker {getSortIcon('ticker')}</th>
                     
                     {ledgerAssetClass === 'All' && <th style={{padding: '12px'}}>Asset</th>}

                     {ledgerAssetClass === 'Options' && (
                       <>
                         <th style={{padding: '12px'}}>Call / Put</th>
                         <th style={{padding: '12px'}}>Exp. Date</th>
                         <th style={{padding: '12px'}}>Strike Price</th>
                       </>
                     )}
                     
                     {['Stocks', 'Futures', 'Crypto'].includes(ledgerAssetClass) && (
                       <th style={{padding: '12px'}}>L / S</th>
                     )}
                     
                     {ledgerAssetClass === 'Futures' && (
                       <th style={{padding: '12px'}}>Mult.</th>
                     )}
                     
                     <th style={{padding: '12px'}}>{ledgerAssetClass === 'Stocks' ? 'Shares' : (ledgerAssetClass === 'Crypto' ? 'Qty' : (ledgerAssetClass === 'Futures' ? 'Contracts' : 'QTY'))}</th>
                     <th style={{padding: '12px'}}>Avg. Entry</th>
                     <th style={{padding: '12px'}}>Avg. Exit</th>
                     <th onClick={() => requestSort('pnl')} style={{padding: '12px', cursor: 'pointer', userSelect: 'none'}}>Net P&L (%) {getSortIcon('pnl')}</th>
                     <th style={{padding: '12px'}}>Time in Trade</th>
                     <th style={{padding: '12px', textAlign: 'right'}}>Action</th>
                   </tr>
                 </thead>
                 <tbody>
                   {processedTrades.map(t => {
                     let rowBg = 'transparent';
                     if (t.status === 'Closed') rowBg = t.pnl >= 0 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)';
                     
                     const asset = t.assetClass || 'Options';
                     const qtyLabel = Number(t.qty || t.contracts || 1);
                     
                     const totalExited = t.exits.reduce((sum, e) => sum + Number(e.qty || e.contracts || 0), 0);
                     const totalExitValue = t.exits.reduce((sum, e) => sum + (Number(e.price) * Number(e.qty || e.contracts || 0)), 0);
                     const avgExitPrice = totalExited > 0 ? (totalExitValue / totalExited).toFixed(2) : '-';
                     const lastExit = t.exits.length > 0 ? t.exits[t.exits.length - 1] : null;
                     const duration = lastExit ? getDuration(t.date, t.entryTime, lastExit.date, lastExit.time) : 'Open';
                     
                     const multiplier = asset === 'Options' ? 100 : (asset === 'Futures' ? Number(t.multiplier || 1) : 1);
                     const costBasis = (t.entry * totalExited * multiplier) + (Number(t.commission || 0) * (totalExited / qtyLabel));
                     const percentReturn = costBasis > 0 ? ((t.pnl / costBasis) * 100).toFixed(1) : 0;
                     
                     return (
                       <tr key={t.id} style={{borderBottom: '1px solid var(--border)', backgroundColor: rowBg}}>
                         <td style={{padding: '12px', textAlign: 'left'}}>
                           <div style={{color: 'var(--text-main)', fontWeight: 'bold', fontSize: '13px'}}>{formatDateStr(t.date)}</div>
                           <div style={{color: 'var(--text-muted)', fontSize: '12px'}}>{formatTimeStr(t.entryTime)}</div>
                         </td>
                         <td style={{padding: '12px', fontWeight: 'bold', color: 'var(--text-main)'}}>{t.ticker}</td>
                         
                         {ledgerAssetClass === 'All' && <td style={{padding: '12px', fontSize: '13px', color: 'var(--text-muted)'}}>{asset}</td>}

                         {ledgerAssetClass === 'Options' && (
                           <>
                             <td style={{padding: '12px', color: t.type === 'Call' ? '#10b981' : '#ef4444', fontWeight: 'bold'}}>{t.type}</td>
                             <td style={{padding: '12px', fontSize: '13px', color: 'var(--text-muted)'}}>{t.expiration ? formatDateStr(t.expiration) : '-'}</td>
                             <td style={{padding: '12px'}}>${t.strike}</td>
                           </>
                         )}
                         
                         {['Stocks', 'Futures', 'Crypto'].includes(ledgerAssetClass) && (
                           <td style={{padding: '12px', color: t.direction === 'Long' ? '#10b981' : '#ef4444', fontWeight: 'bold'}}>{t.direction}</td>
                         )}
                         
                         {ledgerAssetClass === 'Futures' && (
                           <td style={{padding: '12px', fontSize: '13px'}}>x{t.multiplier}</td>
                         )}

                         <td style={{padding: '12px'}}>{qtyLabel}</td>
                         <td style={{padding: '12px'}}>{`$${t.entry.toFixed(2)}`}</td>
                         <td style={{padding: '12px'}}>{avgExitPrice !== '-' ? `$${avgExitPrice}` : '-'}</td>
                         <td className={t.status === 'Open' && t.pnl === 0 ? '' : (t.pnl >= 0 ? 'text-green' : 'text-red')} style={{padding: '12px', fontWeight:'bold'}}>
                            <div>{t.status === 'Open' && totalExited === 0 ? 'Open' : formatMoney(t.pnl)}</div>
                            {totalExited > 0 && (<div style={{fontSize: '12px', marginTop: '4px', opacity: 0.9}}>{percentReturn > 0 ? `+${percentReturn}%` : `${percentReturn}%`}</div>)}
                         </td>
                         <td style={{padding: '12px', fontSize: '13px', color: 'var(--text-muted)'}}>{duration !== 'Open' ? `⏱️ ${duration}` : <span style={{color: '#ff9800', fontWeight: 'bold'}}>Open</span>}</td>
                         <td style={{padding: '12px', textAlign: 'right'}}>
                           <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
                             <button onClick={() => handleEditClick(t)} style={{background: 'var(--primary)', color: '#000', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold'}}>Edit</button>
                             <button onClick={() => deleteTrade(t.id)} style={{background: 'var(--danger)', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'}}>Del</button>
                           </div>
                         </td>
                       </tr>
                     );
                   })}
                   {processedTrades.length === 0 && ( <tr><td colSpan="12" style={{padding: '20px', textAlign: 'center', color: 'var(--text-muted)'}}>No trades found for this filter.</td></tr> )}
                 </tbody>
               </table>
             </div>
           </div>
        )}

        {/* --- ANALYTICS DASHBOARD --- */}
        {activeTab === 'analytics' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div>
              <h2 style={{marginTop: 0, marginBottom: '8px'}}>Edge & Execution Analytics</h2>
              <p style={{color: 'var(--text-muted)', margin: 0}}>Review your psychological leaks and find where you are leaving money on the table.</p>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
               <div className="card" style={{ display: 'flex', flexDirection: 'column', height: '320px' }}>
                 <h3 style={{marginTop: 0, color: 'var(--text-main)', marginBottom: '4px'}}>Execution Mistakes</h3>
                 <p style={{color: 'var(--text-muted)', fontSize: '13px', marginBottom: '16px'}}>Percentage of total closed trades ({executionStats.totalClosed}) featuring these errors.</p>
                 
                 <div style={{display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, justifyContent: 'center'}}>
                   <div>
                     <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px', fontWeight: 'bold'}}>
                       <span>Entry Too Early</span>
                       <span>{executionStats.entryEarly} <span style={{color:'var(--text-muted)', fontWeight:'normal'}}>({executionStats.entryEarlyPct}%)</span></span>
                     </div>
                     <div style={{height: '8px', background: 'var(--bg-color)', borderRadius: '4px', overflow: 'hidden'}}>
                       <div style={{height: '100%', width: `${executionStats.entryEarlyPct}%`, background: '#f59e0b', borderRadius: '4px'}}></div>
                     </div>
                   </div>

                   <div>
                     <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px', fontWeight: 'bold'}}>
                       <span>Entry Too Late</span>
                       <span>{executionStats.entryLate} <span style={{color:'var(--text-muted)', fontWeight:'normal'}}>({executionStats.entryLatePct}%)</span></span>
                     </div>
                     <div style={{height: '8px', background: 'var(--bg-color)', borderRadius: '4px', overflow: 'hidden'}}>
                       <div style={{height: '100%', width: `${executionStats.entryLatePct}%`, background: '#f97316', borderRadius: '4px'}}></div>
                     </div>
                   </div>

                   <div>
                     <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px', fontWeight: 'bold'}}>
                       <span>Sold Too Early</span>
                       <span>{executionStats.soldEarly} <span style={{color:'var(--text-muted)', fontWeight:'normal'}}>({executionStats.soldEarlyPct}%)</span></span>
                     </div>
                     <div style={{height: '8px', background: 'var(--bg-color)', borderRadius: '4px', overflow: 'hidden'}}>
                       <div style={{height: '100%', width: `${executionStats.soldEarlyPct}%`, background: '#8b5cf6', borderRadius: '4px'}}></div>
                     </div>
                   </div>

                   <div>
                     <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px', fontWeight: 'bold'}}>
                       <span>Sold Too Late</span>
                       <span>{executionStats.soldLate} <span style={{color:'var(--text-muted)', fontWeight:'normal'}}>({executionStats.soldLatePct}%)</span></span>
                     </div>
                     <div style={{height: '8px', background: 'var(--bg-color)', borderRadius: '4px', overflow: 'hidden'}}>
                       <div style={{height: '100%', width: `${executionStats.soldLatePct}%`, background: '#ec4899', borderRadius: '4px'}}></div>
                     </div>
                   </div>
                 </div>
               </div>

               <div className="card" style={{ height: '320px', display: 'flex', flexDirection: 'column' }}>
                  <div className="card-title" style={{ marginBottom: 'auto' }}>Frequency of Mistakes</div>
                  <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
                    <Radar data={executionChartData} options={executionRadarOptions} />
                  </div>
               </div>
            </div>

            <div className="card" style={{ height: '320px', display: 'flex', flexDirection: 'column' }}>
               <div className="card-title" style={{ marginBottom: 'auto' }}>Strategy Net Edge</div>
               <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
                 <Radar data={charts.edge} options={analyticsRadarOptions} />
               </div>
            </div>
          </div>
        )}

        {activeTab === 'playbook' && (
          <div>
            <h2 style={{marginTop: 0}}>Trading Playbook (Mini-Course)</h2>
            <p style={{color: 'var(--text-muted)', marginBottom: '32px', maxWidth: '800px'}}>Stop guessing. Trade the setup. Below are the core strategies you should be executing. Master one before moving to the next. Consistency comes from executing the exact same rules flawlessly over a large sample size.</p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px', maxWidth: '900px' }}>
              
              <div className="card" style={{borderLeft: '4px solid var(--primary)'}}>
                <h3 style={{ color: 'var(--text-main)', marginTop: 0, fontSize: '20px' }}>Module 1: Breakout / Breakdown 🚀</h3>
                <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>📖 The Concept:</strong> Price consolidates against a heavy resistance (ceiling) or support (floor) level. As pressure builds, the eventual break of that level forces trapped traders to cover, creating a massive momentum spike.</p>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>⚡ Execution:</strong> Do not front-run. Wait for a strong 5m or 15m candle to close *past* the level. Better yet, wait for the breakout, let it pull back to retest the level as new support, and enter on the bounce.</p>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>⚙️ Options Strategy:</strong> High Gamma is your friend here. Buy At-The-Money (ATM) or 1-strike Out-Of-The-Money (OTM) contracts with 0-3 Days to Expiration (DTE). The explosive move will rapidly increase the premium.</p>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>📉 Risk Management:</strong> Hard stop placed just inside the breakout level. If it falls back into the prior range, it's a "fake-out" and the thesis is instantly invalidated. Cut it immediately.</p>
                  <div style={{ background: 'var(--bg-color)', padding: '12px', borderRadius: '6px', fontSize: '13px', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
                    <strong>📊 Real-World Example:</strong> SPY consolidates at $510 for two hours. It breaks $510.15 on high volume. You buy $510 Calls. Your stop loss is if SPY drops back below $509.80.
                  </div>
                </div>
              </div>

              <div className="card" style={{borderLeft: '4px solid var(--primary)'}}>
                <h3 style={{ color: 'var(--text-main)', marginTop: 0, fontSize: '20px' }}>Module 2: Support / Resistance Bounce 🛡️</h3>
                <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>📖 The Concept:</strong> Trading the channel. Stocks rarely move in straight lines; they bounce between historical supply (sellers) and demand (buyers) zones.</p>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>⚡ Execution:</strong> Let the price come to you. Wait for price to tap the zone and print a reversal candlestick (like a bottom hammer or a bullish engulfing candle) on the 5m chart before entering.</p>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>⚙️ Options Strategy:</strong> Bounces can take time to develop. Buy ITM (In-The-Money) or ATM contracts and give yourself 7-14 DTE to avoid getting crushed by Theta (time decay) if the stock chops around the support level.</p>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>📉 Risk Management:</strong> The stop loss goes just below the wick of the reversal candle. This offers an elite Risk-to-Reward ratio (e.g., risking $50 to make $300).</p>
                  <div style={{ background: 'var(--bg-color)', padding: '12px', borderRadius: '6px', fontSize: '13px', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
                    <strong>📊 Real-World Example:</strong> TSLA hits a known daily support level of $170 and forms a hammer. You buy $170 Calls. You set your stop loss right below the hammer's wick at $169.50.
                  </div>
                </div>
              </div>

              <div className="card" style={{borderLeft: '4px solid var(--primary)'}}>
                <h3 style={{ color: 'var(--text-main)', marginTop: 0, fontSize: '20px' }}>Module 3: Trend Continuation (Flags) 📈</h3>
                <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>📖 The Concept:</strong> The trend is your friend. After a massive move up, a stock needs to rest. It will pull back slowly on low volume (forming a "flag" pattern) before continuing the macro trend.</p>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>⚡ Execution:</strong> Draw a descending trendline over the top of the pullback candles. Enter the trade the moment volume steps back in and breaks that mini-trendline.</p>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>⚙️ Options Strategy:</strong> Use a .60 to .70 Delta (ITM) contract. You want the option to move closely with the stock price as the trend resumes.</p>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>📉 Risk Management:</strong> Stop loss goes directly under the lowest point of the pullback flag. If it breaks that low, it's no longer a resting phase—it's a reversal.</p>
                  <div style={{ background: 'var(--bg-color)', padding: '12px', borderRadius: '6px', fontSize: '13px', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
                    <strong>📊 Real-World Example:</strong> NVDA surges at the open, then drifts lower for 30 minutes on low volume. It breaks the drift trendline. You buy calls. Stop goes below the drift's absolute bottom.
                  </div>
                </div>
              </div>

              <div className="card" style={{borderLeft: '4px solid var(--primary)'}}>
                <h3 style={{ color: 'var(--text-main)', marginTop: 0, fontSize: '20px' }}>Module 4: Opening Range Breakout (ORB) 🌅</h3>
                <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>📖 The Concept:</strong> The first 15 minutes of the market open are filled with institutional positioning. Once the high and low of that initial period are set, a break out of that range usually sets the trend for the morning.</p>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>⚡ Execution:</strong> Let the first 15 minutes pass. Draw a line at the highest high and lowest low. If a candle closes above the high, go long. If it closes below the low, go short.</p>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>⚙️ Options Strategy:</strong> highly liquid ATM options (like SPY or QQQ 0DTEs). The spreads need to be incredibly tight because this is a fast momentum trade.</p>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>📉 Risk Management:</strong> Because this is highly volatile, size down your contracts. Stop loss is usually placed at the VWAP (Volume Weighted Average Price) or the midpoint of the 15-min range.</p>
                  <div style={{ background: 'var(--bg-color)', padding: '12px', borderRadius: '6px', fontSize: '13px', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
                    <strong>📊 Real-World Example:</strong> QQQ's 15-min high is $445. At 9:47 AM, price rips through $445. You buy $445 Calls. You exit if it drops back to VWAP.
                  </div>
                </div>
              </div>

              <div className="card" style={{borderLeft: '4px solid var(--primary)'}}>
                <h3 style={{ color: 'var(--text-main)', marginTop: 0, fontSize: '20px' }}>Module 5: Mean Reversion 🧲</h3>
                <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>📖 The Concept:</strong> The rubber band effect. When price gets too far extended away from its core moving averages (like VWAP or the 20 EMA) in a straight line, it tends to snap back to the mean.</p>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>⚡ Execution:</strong> Never just "guess" the bottom. Wait for indicator divergence (e.g., price makes a new low, but RSI makes a higher low) combined with a strong reversal candle back toward the moving average.</p>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>⚙️ Options Strategy:</strong> Buy ITM. Reversals can sometimes be slow and choppy at first as the buyers fight the sellers. You want intrinsic value protecting you.</p>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>📉 Risk Management:</strong> Place the stop loss slightly past the absolute extreme of the prior trend. If it breaks that low, the trend isn't over.</p>
                  <div style={{ background: 'var(--bg-color)', padding: '12px', borderRadius: '6px', fontSize: '13px', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
                    <strong>📊 Real-World Example:</strong> AAPL drops 5% in a straight line, far below VWAP. RSI hits 15. A massive green engulfing candle forms. You buy calls targeting a return to VWAP.
                  </div>
                </div>
              </div>

              <div className="card" style={{borderLeft: '4px solid var(--primary)'}}>
                <h3 style={{ color: 'var(--text-main)', marginTop: 0, fontSize: '20px' }}>Module 6: News / Earnings 📰</h3>
                <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>📖 The Concept:</strong> Trading exogenous events, fed speeches, or earnings reports that cause massive institutional re-pricing of a stock.</p>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>⚡ Execution:</strong> *Never hold naked options into an earnings print.* It's a coin-flip. Let the news drop, wait 10-15 minutes for the algorithmic volatility to shake out, and trade the trend that establishes *after* the dust settles.</p>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>⚙️ Options Strategy:</strong> Be incredibly aware of IV (Implied Volatility) Crush. Buying options right before a scheduled news event is dangerous because premium prices are inflated. Trading *after* the event is safer.</p>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}><strong style={{color:'var(--primary)'}}>📉 Risk Management:</strong> Extremely tight stops. News can cause violent whipsaws. Size down your capital allocation by 50% for these setups.</p>
                  <div style={{ background: 'var(--bg-color)', padding: '12px', borderRadius: '6px', fontSize: '13px', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
                    <strong>📊 Real-World Example:</strong> AMZN beats earnings but gaps down. You wait 15 mins. Buyers step in and start reclaiming support. You go long with a tight stop at the low of the day.
                  </div>
                </div>
              </div>

            </div>
          </div>
        )}

        {/* --- SETTINGS REDESIGN --- */}
        {activeTab === 'settings' && (
          <div style={{ width: '100%' }}>
            <h2 style={{marginTop: 0, marginBottom: '24px'}}>Settings</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px', alignItems: 'start' }}>
              
              <div className="card" style={{ border: '1px solid #3b82f6', background: 'rgba(59, 130, 246, 0.05)', display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ marginTop: 0, color: '#3b82f6' }}>Data Management (Backup & Restore)</h3>
                <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '16px' }}>
                  Because Trade Knight runs locally in your browser, clearing your cookies will erase your data. Export a backup frequently.
                </p>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <button onClick={handleExportData} style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
                    📥 Export Backup (.json)
                  </button>
                  <div>
                    <input type="file" accept=".json" style={{ display: 'none' }} ref={fileInputRef} onChange={handleImportData} />
                    <button onClick={() => fileInputRef.current.click()} style={{ background: 'transparent', color: 'var(--text-main)', border: '1px solid var(--border)', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
                      📤 Import Backup
                    </button>
                  </div>
                </div>
              </div>

              <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ marginTop: 0, color: 'var(--text-main)' }}>Account Settings</h3>
                <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Update details for your currently selected portfolio.</p>
                <form onSubmit={handleUpdateAccount}>
                  <div style={{ marginTop: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>Account Nickname</label>
                    <input type="text" style={{ boxSizing: 'border-box', width: '100%', padding: '12px', borderRadius: '6px', background: 'var(--bg-color)', color: 'var(--text-main)', border: '1px solid var(--border)', fontSize: '16px', marginBottom: '16px' }} value={accountForm.name} onChange={e => setAccountForm({...accountForm, name: e.target.value})} required />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>Starting Capital ($)</label>
                    <input type="number" step="any" style={{ boxSizing: 'border-box', width: '100%', padding: '12px', borderRadius: '6px', background: 'var(--bg-color)', color: 'var(--text-main)', border: '1px solid var(--border)', fontSize: '16px', marginBottom: '16px' }} value={accountForm.startingCapital} onChange={e => setAccountForm({...accountForm, startingCapital: Number(e.target.value)})} required />
                  </div>
                  <button type="submit" className="log-trade-btn">Save Changes</button>
                </form>
              </div>

              <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ marginTop: 0, color: 'var(--primary)' }}>Manage Portfolios</h3>
                <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Create a new account to track a separate portfolio or challenge. All data is isolated per account.</p>
                <button onClick={handleCreateAccount} style={{ background: 'var(--bg-color)', color: 'var(--text-main)', border: '1px solid var(--primary)', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', marginTop: '16px', width: 'fit-content' }}>+ Create New Account</button>
                
                {accounts.length > 1 && (
                  <button onClick={handleDeleteAccount} style={{ background: 'transparent', color: 'var(--danger)', border: '1px solid var(--danger)', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', marginTop: '16px', width: 'fit-content' }}>Delete Current Account</button>
                )}
              </div>

              <div className="card" style={{ border: '1px solid var(--danger)', display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ marginTop: 0, color: 'var(--danger)' }}>Danger Zone</h3>
                <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Irreversibly delete all trades across all accounts from local storage.</p>
                <button onClick={factoryReset} style={{ background: 'var(--danger)', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', marginTop: '16px', width: 'fit-content' }}>Factory Reset (Wipe Everything)</button>
              </div>

            </div>
          </div>
        )}
      </main>

      {/* MODAL OVERLAY */}
      {isModalOpen && (
        <div className="modal-overlay" style={{padding: '20px', overflowY: 'auto', zIndex: 1000}}>
          <div className="modal-content" style={{maxWidth: '650px'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
               <h2 style={{margin: 0}}>{editingId ? 'EDIT TRADE' : 'LOG TRADE'}</h2>
               
               {/* ASSET CLASS SELECTOR */}
               <div style={{display: 'flex', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '4px'}}>
                 {['Options', 'Stocks', 'Futures', 'Crypto'].map(ac => (
                   <button 
                     key={ac} type="button" 
                     onClick={() => setFormData({...formData, assetClass: ac})}
                     style={{
                       background: formData.assetClass === ac ? 'var(--primary)' : 'transparent',
                       color: formData.assetClass === ac ? '#000' : 'var(--text-muted)',
                       border: 'none', padding: '6px 12px', borderRadius: '6px', 
                       cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', transition: 'all 0.2s'
                     }}
                   >
                     {ac}
                   </button>
                 ))}
               </div>
            </div>

            <form onSubmit={handleSaveTrade}>
              
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px', borderBottom: '1px solid var(--border)', paddingBottom: '16px'}}>
                <div className="form-group"><label>Entry Date & Time</label><div style={{display: 'flex', gap: '8px'}}><input type="date" className="form-control" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} required /><input type="time" className="form-control" value={formData.entryTime} onChange={e => setFormData({...formData, entryTime: e.target.value})} required /></div></div>
                <div className="form-group"><label>Ticker</label><input type="text" className="form-control" placeholder="e.g. SPY" value={formData.ticker} onChange={e => setFormData({...formData, ticker: e.target.value.toUpperCase()})} required /></div>
                
                {/* CONDITIONAL FIELDS BASED ON ASSET CLASS */}
                {formData.assetClass === 'Options' && (
                  <>
                    <div className="form-group"><label>Strike Price ($)</label><input type="number" step="any" className="form-control" placeholder="e.g. 515.5" value={formData.strike} onChange={e => setFormData({...formData, strike: e.target.value})} required /></div>
                    <div className="form-group">
                       <label style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                         Expiration Date
                         <button type="button" onClick={() => setFormData({...formData, expiration: formData.date})} style={{background: 'var(--primary)', color: '#000', border: 'none', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', cursor: 'pointer'}}>0DTE</button>
                       </label>
                       <input type="date" className="form-control" value={formData.expiration} onChange={e => setFormData({...formData, expiration: e.target.value})} required />
                    </div>
                    <div className="form-group"><label>Type</label><select className="form-control" value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})}><option>Call</option><option>Put</option></select></div>
                  </>
                )}

                {['Stocks', 'Futures', 'Crypto'].includes(formData.assetClass) && (
                   <div className="form-group"><label>Direction</label><select className="form-control" value={formData.direction} onChange={e => setFormData({...formData, direction: e.target.value})}><option>Long</option><option>Short</option></select></div>
                )}

                {formData.assetClass === 'Futures' && (
                   <div className="form-group"><label>Point Multiplier ($ per pt)</label><input type="number" step="any" className="form-control" placeholder="e.g. 50" value={formData.multiplier} onChange={e => setFormData({...formData, multiplier: e.target.value})} required /></div>
                )}

                <div className="form-group">
                  <label>Strategy / Setup</label>
                  <select className="form-control" value={formData.setup} onChange={e => setFormData({...formData, setup: e.target.value})}>
                    {SETUP_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>{formData.assetClass === 'Options' || formData.assetClass === 'Futures' ? 'Contracts' : (formData.assetClass === 'Stocks' ? 'Shares' : 'Quantity')}</label>
                  <input type="number" step={formData.assetClass === 'Crypto' ? 'any' : '1'} className="form-control" min="0" value={formData.qty} onChange={e => setFormData({...formData, qty: parseFloat(e.target.value)})} required />
                </div>
                
                <div className="form-group"><label>Avg Entry Price ($)</label><input type="number" step="any" className="form-control" value={formData.entry} onChange={e => setFormData({...formData, entry: e.target.value})} required /></div>
                <div className="form-group"><label>Total Commissions & Fees ($)</label><input type="number" step="0.01" min="0" className="form-control" placeholder="0.00" value={formData.commission} onChange={e => setFormData({...formData, commission: e.target.value})} /></div>
              </div>

              <div style={{background: 'rgba(255,255,255,0.02)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border)', marginBottom: '16px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
                  <h4 style={{margin: 0, color: 'var(--text-main)'}}>Realized Exits ({formData.exits.reduce((sum, e) => sum + Number(e.qty||e.contracts||0), 0)} / {formData.qty} {formData.assetClass === 'Stocks' ? 'Shares' : (formData.assetClass === 'Crypto' ? 'Qty' : 'Contracts')})</h4>
                  <button type="button" onClick={addExit} style={{background: 'var(--primary)', color: '#000', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold'}}>+ Add Exit</button>
                </div>
                {formData.exits.map((ex, i) => (
                  <div key={i} style={{display: 'grid', gridTemplateColumns: '1.5fr 1.2fr 1.2fr 1fr auto', gap: '8px', marginBottom: '10px', alignItems: 'center'}}>
                    <input type="date" className="form-control" value={ex.date} onChange={e => updateExit(i, 'date', e.target.value)} required />
                    <input type="time" className="form-control" value={ex.time} onChange={e => updateExit(i, 'time', e.target.value)} required />
                    <input type="number" step="any" className="form-control" placeholder="Price" value={ex.price} onChange={e => updateExit(i, 'price', e.target.value)} required />
                    <input type="number" step={formData.assetClass === 'Crypto' ? 'any' : '1'} className="form-control" placeholder="Qty" min="0" value={ex.qty || ex.contracts || ''} onChange={e => updateExit(i, 'qty', e.target.value)} required />
                    <button type="button" onClick={() => removeExit(i)} style={{background: 'var(--danger)', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold'}}>X</button>
                  </div>
                ))}
                {formData.exits.length === 0 && ( <p style={{fontSize: '14px', color: 'var(--text-muted)', fontStyle: 'italic', margin: 0, textAlign: 'center', padding: '10px 0'}}>Position is fully open. Click "+ Add Exit" to scale out.</p> )}
              </div>

              {/* Execution Review Checkboxes */}
              <div style={{ marginBottom: '24px' }}>
                 <h4 style={{margin: '0 0 12px 0', color: 'var(--text-main)'}}>Execution Review Tags</h4>
                 <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px'}}>
                    <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '14px', background: 'var(--bg-color)', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)'}}>
                       <input type="checkbox" checked={formData.entryEarly} onChange={e => setFormData({...formData, entryEarly: e.target.checked})} style={{cursor: 'pointer'}} />
                       Entry Too Early
                    </label>
                    <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '14px', background: 'var(--bg-color)', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)'}}>
                       <input type="checkbox" checked={formData.entryLate} onChange={e => setFormData({...formData, entryLate: e.target.checked})} style={{cursor: 'pointer'}} />
                       Entry Too Late
                    </label>
                    <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '14px', background: 'var(--bg-color)', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)'}}>
                       <input type="checkbox" checked={formData.soldEarly} onChange={e => setFormData({...formData, soldEarly: e.target.checked})} style={{cursor: 'pointer'}} />
                       Sold Too Early
                    </label>
                    <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '14px', background: 'var(--bg-color)', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)'}}>
                       <input type="checkbox" checked={formData.soldLate} onChange={e => setFormData({...formData, soldLate: e.target.checked})} style={{cursor: 'pointer'}} />
                       Sold Too Late
                    </label>
                 </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => { setIsModalOpen(false); setEditingId(null); }}>Cancel</button>
                <button type="submit" className="log-trade-btn">{editingId ? 'Update Trade' : 'Save Trade'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
